import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { maLoi } from '../../../lib/errors';
import type { ApiError } from '../../../lib/errors';
import { tenantRepository } from '../../../lib/repositories/tenant.repository';
import { useToast } from '../../../lib/toast';
import { TRANG_THAI_TENANT } from '../constants';
import { dangTrienKhai } from '../nghiep-vu/thao-tac';
import type { Tenant, TenantCreate, TenantDetail, TenantDomain, TenantUpdate } from '../types';

/**
 * Khoá cache. Mọi khoá bắt đầu bằng `['tenants']`, nên phân hệ khác (Gói dịch
 * vụ) vô hiệu hoá cả nhóm bằng một khoá mà không phải biết cấu trúc bên trong.
 */
export const KHOA_TENANT = {
  tatCa: ['tenants'] as const,
  danhSach: ['tenants', 'danh-sach'] as const,
  chiTiet: (id: string) => ['tenants', 'chi-tiet', id] as const,
};

/**
 * Lỗi 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt
 * người dùng chờ thêm mới thấy "không tìm thấy" hay "không có quyền".
 */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Danh sách tenant. Màn mở đầu tiên mỗi sáng: ai đang phục vụ, ai đang kẹt. */
export function useTenants() {
  return useQuery<Tenant[]>({
    queryKey: KHOA_TENANT.danhSach,
    queryFn: tenantRepository.danhSach,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/**
 * Chi tiết tenant, kèm tiến độ provisioning.
 *
 * Tự hỏi lại mỗi 5 giây **chỉ khi** tenant đang PROVISIONING: tiến độ nằm
 * ngay trong chi tiết (`provisioning_steps`), hỏi mãi một tenant đã xong là
 * tốn request mà không đổi gì. Workflow xong thì trạng thái rời PROVISIONING
 * và vòng hỏi tự dừng.
 */
export function useTenant(id: string) {
  return useQuery<TenantDetail>({
    queryKey: KHOA_TENANT.chiTiet(id),
    queryFn: () => tenantRepository.chiTiet(id),
    staleTime: 30_000,
    retry: thuLai,
    refetchInterval: (query) =>
      query.state.data && dangTrienKhai(query.state.data.status) ? 5_000 : false,
  });
}

/** Ghi kết quả vào cache chi tiết ngay, rồi để danh sách tải lại. */
function useGhiXong() {
  const queryClient = useQueryClient();
  return (tenant: TenantDetail) => {
    queryClient.setQueryData(KHOA_TENANT.chiTiet(tenant.tenant_id), tenant);
    void queryClient.invalidateQueries({ queryKey: KHOA_TENANT.danhSach });
  };
}

/**
 * `409 TENANT_STATUS_CONFLICT`: trạng thái vừa bị yêu cầu khác đổi (ADR-0025
 * mục 4). Tải lại cả nhóm để nút và nhãn trên màn hình nói đúng trạng thái
 * mới, thay vì để người dùng bấm lại một thao tác chắc chắn hỏng.
 */
function useKhiXungDot() {
  const queryClient = useQueryClient();
  return (err: unknown) => {
    if (maLoi(err) === 'TENANT_STATUS_CONFLICT') {
      void queryClient.invalidateQueries({ queryKey: KHOA_TENANT.tatCa });
    }
  };
}

/**
 * Tạo tenant (`POST`, bắt buộc `Idempotency-Key`).
 *
 * Khoá đi theo **nội dung**: gửi lại đúng nội dung cũ (mất mạng, bấm hai lần,
 * đóng hộp rồi mở lại) thì dùng lại khoá cũ, server trả kết quả đã lưu thay vì
 * tạo tenant thứ hai. Nội dung đổi thì khoá mới, vì dùng lại khoá cho nội dung
 * khác là `422 IDEMPOTENCY_KEY_REUSED`. Tạo xong thì bỏ khoá.
 *
 * Lỗi **không** hiện toast: form giữ nguyên và hiện lỗi ngay trong hộp thoại.
 */
export function useTaoTenant() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  const lanGui = useRef<{ noiDung: string; khoa: string } | null>(null);

  return useMutation({
    mutationFn: (du: TenantCreate) => {
      const noiDung = JSON.stringify(du);
      const truoc = lanGui.current;
      const khoa = truoc?.noiDung === noiDung ? truoc.khoa : crypto.randomUUID();
      lanGui.current = { noiDung, khoa };
      return tenantRepository.tao(du, khoa);
    },
    onSuccess: (tenant) => {
      lanGui.current = null;
      ghiXong(tenant);
      toast.success(t('tenant.daTao', { ten: tenant.display_name }));
    },
  });
}

export function useSuaTenant() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  const khiXungDot = useKhiXungDot();

  return useMutation({
    mutationFn: ({ id, du }: { id: string; du: TenantUpdate }) => tenantRepository.sua(id, du),
    onSuccess: (tenant) => {
      ghiXong(tenant);
      toast.success(t('tenant.daLuu', { ten: tenant.display_name }));
    },
    onError: khiXungDot,
  });
}

/**
 * Duyệt hồ sơ DRAFT và khởi động onboarding (UC-02).
 *
 * Kết quả mang cả tenant mới lẫn `job_id`, nên ghi thẳng vào cache chi tiết:
 * màn hình sang PROVISIONING ngay, và vòng hỏi lại 5 giây của `useTenant` tự
 * bật để tiến độ chạy mà không phải bấm làm mới.
 *
 * Lỗi **không** hiện toast: `409` và `422 TENANT_PROFILE_INCOMPLETE` hiện trong
 * hộp xác nhận, nơi người dùng vừa bấm và còn đọc được câu "bổ sung gì rồi duyệt lại".
 */
export function useDuyetTenant() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  const khiXungDot = useKhiXungDot();

  return useMutation({
    mutationFn: (id: string) => tenantRepository.duyet(id),
    onSuccess: ({ tenant }) => {
      ghiXong(tenant);
      toast.success(
        tenant.deployment_mode === 'ON_PREMISE'
          ? t('tenant.daDuyetOnPremise', { ten: tenant.display_name })
          : t('tenant.daDuyetSaas', { ten: tenant.display_name }),
      );
    },
    onError: khiXungDot,
  });
}

export type HanhDongTrangThai = 'tam-ngung' | 'khoi-phuc';

/** Tạm ngưng hoặc khôi phục — hai endpoint, một hộp xác nhận. */
export function useDoiTrangThai() {
  const toast = useToast();
  const { t } = useT();
  const ghiXong = useGhiXong();
  const khiXungDot = useKhiXungDot();

  return useMutation({
    mutationFn: ({ id, hanhDong }: { id: string; hanhDong: HanhDongTrangThai }) =>
      hanhDong === 'tam-ngung' ? tenantRepository.tamNgung(id) : tenantRepository.khoiPhuc(id),
    onSuccess: (tenant, { hanhDong }) => {
      ghiXong(tenant);
      if (hanhDong === 'tam-ngung') {
        toast.success(t('tenant.daTamNgung', { ten: tenant.display_name }));
        return;
      }
      // Khôi phục về TRIAL hay ACTIVE do server quyết (ADR-0025 mục 2): nói ra
      // kết quả, vì người bấm thường chờ thấy "Đang hoạt động".
      const cauHinh = TRANG_THAI_TENANT[tenant.status];
      toast.success(
        t('tenant.daKhoiPhuc', {
          ten: tenant.display_name,
          trangThai: cauHinh ? t(cauHinh.khoa) : tenant.status,
        }),
      );
    },
    onError: khiXungDot,
  });
}

/**
 * Sau khi kiểm hay xác minh domain: tải lại chi tiết để bảng domain, bản ghi
 * DNS và tiến độ triển khai nói đúng trạng thái mới. Tenant đang PROVISIONING
 * chờ ở bước 5c thì vòng hỏi 5 giây của `useTenant` tự thấy workflow đi tiếp.
 */
function useTaiLaiChiTiet() {
  const queryClient = useQueryClient();
  return (tenantId: string) =>
    void queryClient.invalidateQueries({ queryKey: KHOA_TENANT.chiTiet(tenantId) });
}

/**
 * Thêm domain email cho tenant (ADR-0082). Lỗi **không** toast: hộp thêm hiện
 * lỗi dưới ô domain và không đóng. Thêm xong tải lại chi tiết để khối bản ghi
 * DNS của domain mới hiện ngay.
 */
export function useThemDomain(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const taiLai = useTaiLaiChiTiet();

  return useMutation({
    mutationFn: ({ domain, khoaLap }: { domain: string; khoaLap: string }) =>
      tenantRepository.themDomain(tenantId, domain, khoaLap),
    onSuccess: (moi: TenantDomain) => {
      taiLai(tenantId);
      toast.success(t('tenant.tdDaThem', { domain: moi.domain }));
    },
  });
}

/** Gỡ domain email (GG-FX-39). Lỗi (domain cuối, còn người dùng) hiện trong hộp xác nhận, không đi toast. */
export function useGoDomain(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const taiLai = useTaiLaiChiTiet();

  return useMutation({
    mutationFn: (domain: string) => tenantRepository.goDomain(tenantId, domain),
    onSuccess: (_kq, domain) => {
      taiLai(tenantId);
      toast.success(t('tenant.gdDaGo', { domain }));
    },
  });
}

/**
 * Kiểm bản ghi DNS TXT của một domain ngay (ADR-0054 mục 2). DNS chưa khớp là
 * kết quả bình thường, không phải lỗi: báo trung tính, không tông đỏ. Lỗi thật
 * (404, 409) không có hộp thoại nào để hiện nên đi toast.
 */
export function useKiemTraDomain(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  const taiLai = useTaiLaiChiTiet();
  const khiXungDot = useKhiXungDot();

  return useMutation({
    mutationFn: (domain: string) => tenantRepository.kiemTraDomain(tenantId, domain),
    onSuccess: (ketQua: TenantDomain) => {
      taiLai(tenantId);
      if (ketQua.verified_at && ketQua.verify_method === 'DNS_TXT') {
        toast.success(t('tenant.dnsDaKhop', { domain: ketQua.domain }));
      } else if (ketQua.verified_at) {
        toast.info(t('tenant.domainDaXacMinhTruoc', { domain: ketQua.domain }));
      } else {
        toast.info(t('tenant.dnsChuaKhop', { domain: ketQua.domain }));
      }
    },
    onError: (err) => {
      khiXungDot(err);
      toast.error(t('tenant.kiemDnsLoi', { loi: dichLoi(err) }));
    },
  });
}

/**
 * Xác minh thủ công (`ADMIN_APPROVAL`, ADR-0054 mục 3). Lỗi **không** hiện
 * toast: hộp xác nhận giữ nguyên lý do vừa nhập và hiện lỗi ngay trong hộp.
 */
export function useXacMinhThuCong(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const taiLai = useTaiLaiChiTiet();
  const khiXungDot = useKhiXungDot();

  return useMutation({
    mutationFn: ({ domain, lyDo }: { domain: string; lyDo: string }) =>
      tenantRepository.xacMinhThuCong(tenantId, domain, { reason: lyDo.trim() }),
    onSuccess: (ketQua: TenantDomain) => {
      taiLai(tenantId);
      toast.success(
        ketQua.verify_method === 'ADMIN_APPROVAL'
          ? t('tenant.daXacMinhThuCong', { domain: ketQua.domain })
          : t('tenant.domainDaXacMinhTruoc', { domain: ketQua.domain }),
      );
    },
    onError: khiXungDot,
  });
}
